// src/services/sharePointLibraryApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

// SharePoint Library Types
export interface SharePointLibrary {
  libraryId: number;
  libraryName: string;
  siteId: string;
  driveId: string | null;
  webUrl: string | null;
  isActive: boolean;
  createdAt: string;
  lastUpdated: string;
  createdBy: string | null;
  lastUpdatedBy: string | null;
}

// Global SharePoint Settings (single system-wide configuration)
export interface GlobalSharePointSettings {
  libraryName: string;
  siteId: string;
  webUrl: string | null;
}

export interface UpdateGlobalSharePointSettingsRequest {
  libraryName: string;
  siteId: string;
  webUrl?: string | null;
}

export interface CreateLibraryRequest {
  libraryName: string;
  siteId: string;
  webUrl?: string;
}

export interface UpdateLibraryRequest {
  libraryName?: string;
  siteId?: string;
  webUrl?: string;
  isActive?: boolean;
}

export interface LibrariesListResponse {
  libraries: SharePointLibrary[];
}

// Error Type
export interface SharePointLibraryApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR';
}

/**
 * Get all SharePoint libraries
 */
export async function getAllSharePointLibraries(): Promise<LibrariesListResponse> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to load libraries: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as SharePointLibraryApiError;
  }

  return response.json();
}

/**
 * Get a single SharePoint library by ID
 */
export async function getSharePointLibrary(libraryId: number): Promise<SharePointLibrary> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries/${libraryId}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Library not found', code: 'NOT_FOUND' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to load library: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as SharePointLibraryApiError;
  }

  return response.json();
}

/**
 * Create a new SharePoint library
 */
export async function createSharePointLibrary(data: CreateLibraryRequest): Promise<SharePointLibrary> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries`;
  
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({
      libraryName: data.libraryName.trim(),
      siteId: data.siteId.trim(),
      webUrl: data.webUrl?.trim() || undefined,
    }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to create library: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as SharePointLibraryApiError;
  }

  return response.json();
}

/**
 * Update an existing SharePoint library
 */
export async function updateSharePointLibrary(
  libraryId: number,
  data: UpdateLibraryRequest
): Promise<SharePointLibrary> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries/${libraryId}`;
  
  // Clean up the data - only include fields that have values
  const cleanedData: UpdateLibraryRequest = {};
  if (data.libraryName !== undefined && data.libraryName !== null && data.libraryName.trim() !== '') {
    cleanedData.libraryName = data.libraryName.trim();
  }
  if (data.siteId !== undefined && data.siteId !== null && data.siteId.trim() !== '') {
    cleanedData.siteId = data.siteId.trim();
  }
  if (data.webUrl !== undefined) {
    cleanedData.webUrl = data.webUrl === null || (typeof data.webUrl === 'string' && data.webUrl.trim() === '') ? undefined : (typeof data.webUrl === 'string' ? data.webUrl.trim() : undefined);
  }
  if (data.isActive !== undefined) {
    cleanedData.isActive = data.isActive;
  }
  
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(cleanedData),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Library not found', code: 'NOT_FOUND' } as SharePointLibraryApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update library: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as SharePointLibraryApiError;
  }

  return response.json();
}

/**
 * Delete (deactivate) a SharePoint library
 */
export async function deleteSharePointLibrary(libraryId: number): Promise<void> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries/${libraryId}`;
  
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Library not found', code: 'NOT_FOUND' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to delete library: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as SharePointLibraryApiError;
  }

  // DELETE endpoint may return { success: true } or nothing
  if (response.status === 204 || response.status === 200) {
    return;
  }

  const result = await response.json();
  return result;
}

/**
 * Get global SharePoint settings (single system-wide configuration)
 */
export async function getGlobalSharePointSettings(): Promise<GlobalSharePointSettings> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-settings`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to load SharePoint settings: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as SharePointLibraryApiError;
  }

  return response.json();
}

/**
 * Update global SharePoint settings (single system-wide configuration)
 */
export async function updateGlobalSharePointSettings(
  data: UpdateGlobalSharePointSettingsRequest
): Promise<GlobalSharePointSettings> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-settings`;
  
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({
      libraryName: data.libraryName.trim(),
      siteId: data.siteId.trim(),
      webUrl: data.webUrl?.trim() || null,
    }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update SharePoint settings: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as SharePointLibraryApiError;
  }

  return response.json();
}

